import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Segmented Control component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 216:1296):
 * - Sizes: Small (py-4px), Medium (py-6px)
 * - Active state: white bg with shadow
 * - Inactive state: transparent bg
 * - Container: 5% black bg with rounded corners
 */
const segmentedControlVariants = cva(
  'inline-flex items-center rounded-lg bg-black/5 p-1 dark:bg-white/5',
  {
    variants: {
      size: {
        sm: 'gap-0.5',
        md: 'gap-1',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const segmentedItemVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm transition-all duration-150 focus:outline-none',
  {
    variants: {
      size: {
        sm: 'px-3 py-1',
        md: 'px-4 py-1.5',
      },
      active: {
        true: 'bg-white font-medium text-grey-950 shadow-[0px_1px_1px_0px_rgba(11,19,36,0.1),0px_1px_3px_0px_rgba(11,19,36,0.1)] dark:bg-grey-800 dark:text-grey-50',
        false: 'font-normal text-grey-950 hover:text-grey-700 dark:text-grey-400 dark:hover:text-grey-200',
      },
    },
    defaultVariants: {
      size: 'md',
      active: false,
    },
  }
);

const SegmentedControlContext = React.createContext({
  size: 'md',
  value: '',
  onValueChange: () => {},
});

const SegmentedControl = React.forwardRef(
  ({ className, size = 'md', value, onValueChange, children, ...props }, ref) => {
    return (
      <SegmentedControlContext.Provider value={{ size, value, onValueChange }}>
        <div
          ref={ref}
          className={cn(segmentedControlVariants({ size }), className)}
          role="tablist"
          {...props}
        >
          {children}
        </div>
      </SegmentedControlContext.Provider>
    );
  }
);
SegmentedControl.displayName = 'SegmentedControl';

const SegmentedControlItem = React.forwardRef(
  ({ className, value, leftIcon, rightIcon, children, ...props }, ref) => {
    const { size, value: selectedValue, onValueChange } = React.useContext(SegmentedControlContext);
    const isActive = value === selectedValue;

    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={isActive}
        className={cn(segmentedItemVariants({ size, active: isActive }), className)}
        onClick={() => onValueChange?.(value)}
        {...props}
      >
        {leftIcon && <span className="shrink-0">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);
SegmentedControlItem.displayName = 'SegmentedControlItem';

export {
  SegmentedControl,
  SegmentedControlItem,
  segmentedControlVariants,
  segmentedItemVariants,
};
